import type { InitiativeWriteInput } from '@complan/contracts';
import { INITIATIVE_FIELD_PATHS } from '@complan/domain';
import type { FieldVisibilityMap } from '@complan/domain';
import { isSectionVisible, type ConditionalSection } from './intake-form-model';
import type { WizardStepId } from './wizard-steps';

/**
 * Which intake step a required input is still empty on, so Step 9 can say what is
 * missing and send the user straight to the step that owns it.
 *
 * ## Why this exists next to the findings, not instead of them
 *
 * The authoritative completeness verdict is the server's: `POST /validate` returns
 * every unmet rule of all four tiers in one pass (R8.12), and the summary already
 * stacks them. But that verdict cannot be the whole story on Step 9 for two
 * reasons:
 *
 * 1. It is debounced and remote. Until the first answer returns — and for as long
 *    as the call keeps failing — `serverEvaluated` stays false and only the three
 *    client syntactic rules apply, so an intake with nothing but a title filled in
 *    reads "No validation issues. This initiative is ready to submit."
 * 2. A finding names its rule, not a destination. The wizard has nine directly
 *    clickable steps and no linear gate, so "a channel and an audience are
 *    required" leaves the user to work out which of the nine holds them.
 *
 * This module answers both locally and cheaply: a presence check per required
 * field plus the step that renders it.
 *
 * ## Nothing here invents a mandatory field
 *
 * Every entry is either marked required in the form itself (`Field required`) or
 * carries a domain rule that already makes it mandatory — the `rule` field records
 * which, so the provenance is auditable and this list cannot quietly drift into a
 * second, parallel definition of "required". Fields with no such grounding are
 * deliberately absent: `milestones` and `dependencies` rows are optional (GV-004
 * constrains milestone rows that exist, it does not demand any; a dependency's
 * owner and due date are optional), and
 * `confidentialityReason` has neither a required marker nor a rule (HV-006 governs
 * *permission* to change confidentiality, not the reason text).
 *
 * A presence check only. Whether a filled-in value is *valid* — a title within 200
 * characters, an end date after its start, exactly one primary platform — stays
 * with the rules that own it; this module never second-guesses them.
 */
export interface RequiredFieldDef {
  /** The logical field path, as {@link INITIATIVE_FIELD_PATHS} names it. */
  readonly path: string;
  /** The step whose body renders the field. */
  readonly step: WizardStepId;
  /**
   * The conditional section the field belongs to, when it is only required while
   * that section is in scope (R5.3). Absent for unconditionally required fields.
   */
  readonly section?: ConditionalSection;
  /**
   * The grounding for treating this field as required: the id of the domain rule
   * that makes it mandatory, or `form:required-marker` when the form itself marks
   * it. Documentation, not behaviour.
   */
  readonly rule: string;
  /** Whether the value is absent. Presence only — never a validity judgement. */
  readonly isMissing: (values: Partial<InitiativeWriteInput>) => boolean;
}

/** True when a text-ish value is absent or blank. */
function isBlank(value: unknown): boolean {
  return typeof value !== 'string' || value.trim().length === 0;
}

/** True when an array-valued field holds nothing. */
function isEmptyList(value: unknown): boolean {
  return !Array.isArray(value) || value.length === 0;
}

/**
 * The required inputs of the intake, in step order so the advisory reads in the
 * same order as the stepper.
 */
export const REQUIRED_FIELDS: readonly RequiredFieldDef[] = [
  {
    path: INITIATIVE_FIELD_PATHS.title,
    step: 'basics',
    rule: 'HV-001',
    isMissing: (values) => isBlank(values.title),
  },
  {
    path: INITIATIVE_FIELD_PATHS.ownerUserId,
    step: 'basics',
    rule: 'HV-002',
    isMissing: (values) => isBlank(values.ownerUserId),
  },
  {
    // Both dates are seeded `''`. HV-003 can only compare a range once both are
    // present, and GV-001 cannot derive a Sales_Cycle without them, so an empty
    // date is a completeness gap those rules are silent about.
    path: INITIATIVE_FIELD_PATHS.startDate,
    step: 'timing',
    rule: 'HV-003',
    isMissing: (values) => isBlank(values.startDate),
  },
  {
    path: INITIATIVE_FIELD_PATHS.endDate,
    step: 'timing',
    rule: 'HV-003',
    isMissing: (values) => isBlank(values.endDate),
  },
  {
    // HV-005/HV-007 require a valid Platform set; GV-002 requires a primary among
    // them. Neither can hold with no Platform at all.
    path: INITIATIVE_FIELD_PATHS.platforms,
    step: 'classification',
    rule: 'HV-005',
    isMissing: (values) => isEmptyList(values.platforms),
  },
  {
    path: INITIATIVE_FIELD_PATHS.priorityId,
    step: 'classification',
    rule: 'form:required-marker',
    isMissing: (values) => isBlank(values.priorityId),
  },
  {
    // GV-003: a channel and an audience are required.
    path: INITIATIVE_FIELD_PATHS.channelIds,
    step: 'audience',
    rule: 'GV-003',
    isMissing: (values) => isEmptyList(values.channelIds),
  },
  {
    path: INITIATIVE_FIELD_PATHS.audienceIds,
    step: 'audience',
    rule: 'GV-003',
    isMissing: (values) => isEmptyList(values.audienceIds),
  },
  {
    // GV-005: Field Force involvement yes with no scope entry. Only in scope while
    // the section is visible, which is what keeps the reported set equal to the
    // rendered set (Property 23) — reporting a field the user cannot see would
    // send them to a step showing only the inactive-section note.
    path: INITIATIVE_FIELD_PATHS.fieldForceScope,
    step: 'fieldForce',
    section: 'fieldForce',
    rule: 'GV-005',
    isMissing: (values) => isEmptyList(values.fieldForceScope),
  },
];

/** A required input that is still empty, and the step that owns it. */
export interface MissingRequiredField {
  readonly path: string;
  readonly step: WizardStepId;
}

/**
 * The required inputs still empty in `values`, in step order.
 *
 * A field belonging to a conditional section is skipped while that section is
 * hidden, so turning Field Force involvement off cannot leave a permanent
 * complaint about a step that renders nothing.
 */
export function missingRequiredFields(
  values: Partial<InitiativeWriteInput>,
  visibility: FieldVisibilityMap,
): readonly MissingRequiredField[] {
  const missing: MissingRequiredField[] = [];
  for (const field of REQUIRED_FIELDS) {
    if (field.section !== undefined && !isSectionVisible(field.section, visibility)) continue;
    if (field.isMissing(values)) missing.push({ path: field.path, step: field.step });
  }
  return missing;
}

/**
 * The steps carrying at least one missing required input, in step order, each with
 * the field paths it owns — the shape the advisory renders as one row per step
 * with a single jump button.
 */
export function missingByStep(
  missing: readonly MissingRequiredField[],
): readonly { readonly step: WizardStepId; readonly paths: readonly string[] }[] {
  const byStep = new Map<WizardStepId, string[]>();
  for (const field of missing) {
    const paths = byStep.get(field.step);
    if (paths) paths.push(field.path);
    else byStep.set(field.step, [field.path]);
  }
  return [...byStep.entries()].map(([step, paths]) => ({ step, paths }));
}

/**
 * How many required inputs each step still owes, keyed by step — what the stepper
 * needs to mark a tab without knowing anything about fields.
 *
 * A step with nothing missing is absent from the map rather than present with `0`,
 * so a caller can treat "has an entry" as "owes input".
 */
export function missingCountByStep(
  missing: readonly MissingRequiredField[],
): ReadonlyMap<WizardStepId, number> {
  const counts = new Map<WizardStepId, number>();
  for (const field of missing) {
    counts.set(field.step, (counts.get(field.step) ?? 0) + 1);
  }
  return counts;
}
